import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useEffect, useRef, useState,
} from '../../lib/teact/teact';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loadPromise: Promise<void> | undefined;

function loadTurnstile() {
  if ((window as any).turnstile) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('CAPTCHA_LOAD_FAILED')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('CAPTCHA_LOAD_FAILED'));
    document.head.append(script);
  });

  return loadPromise;
}

type OwnProps = {
  resetKey: number;
  onTokenChange: (token?: string) => void;
};

const TurnstileWidget: FC<OwnProps> = ({ resetKey, onTokenChange }) => {
  // eslint-disable-next-line no-null/no-null
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const sitekey = process.env.TURNSTILE_SITE_KEY;
    if (!sitekey || !containerRef.current) return undefined;

    let isActive = true;
    let widgetId: string | undefined;
    setHasError(false);
    onTokenChange(undefined);

    void loadTurnstile().then(() => {
      if (!isActive || !containerRef.current) return;

      widgetId = (window as any).turnstile.render(containerRef.current, {
        sitekey,
        action: 'login',
        theme: 'auto',
        callback: (token: string) => onTokenChange(token),
        'error-callback': () => {
          onTokenChange(undefined);
          setHasError(true);
        },
        'expired-callback': () => onTokenChange(undefined),
      });
    }).catch(() => {
      if (isActive) setHasError(true);
    });

    return () => {
      isActive = false;
      if (widgetId !== undefined && (window as any).turnstile) {
        (window as any).turnstile.remove(widgetId);
      }
    };
  }, [onTokenChange, resetKey]);

  if (!process.env.TURNSTILE_SITE_KEY) return undefined;

  return (
    <div className="turnstile-wrapper">
      <div ref={containerRef} />
      {hasError && <div className="turnstile-error">Security verification failed. Please refresh.</div>}
    </div>
  );
};

export default memo(TurnstileWidget);
